import React, {
  useMemo,
  useState,
  createContext,
  useContext,
  useRef,
} from "react";
import {
  ROUNDNESS,
  DEFAULT_ADAPTIVE_RADIUS,
  EVENT,
  KEYS,
} from "@excalidraw/common";
import { canChangeRoundness, isFrameLikeElement } from "@excalidraw/element";

import { newElementWith } from "@excalidraw/element";
import { CaptureUpdateAction } from "@excalidraw/element";

import type { ElementsMap, ExcalidrawElement } from "@excalidraw/element/types";
import type { Scene } from "@excalidraw/element";

import { changeProperty } from "../../actions/actionProperties";
import {
  borderCornersIcon,
  adjustmentsHorizontalIcon,
  radiusTopLeftIcon,
  radiusTopRightIcon,
  radiusBottomLeftIcon,
  radiusBottomRightIcon,
} from "../icons";
import { InlineIcon } from "../InlineIcon";
import { useApp, useExcalidrawSetAppState } from "../App";

import type { AppState } from "../../types";

import type { DragInputCallbackType } from "./DragInput";

// Types for individual corner radius
interface CornerRadiusValues {
  topLeft: number;
  topRight: number;
  bottomLeft: number;
  bottomRight: number;
}

// Context for sharing expanded state and corner values
const CornerRadiusExpandedContext = createContext<{
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  cornerValues: CornerRadiusValues;
  setCornerValue: (corner: keyof CornerRadiusValues, value: number) => void;
  isIndividualMode: boolean;
  setIsIndividualMode: (individual: boolean) => void;
  resetCornerValues: (value: number) => void;
} | null>(null);

interface CornerRadiusProps {
  elements?: readonly ExcalidrawElement[];
  element?: ExcalidrawElement;
  elementsMap: ElementsMap;
  scene: Scene;
  appState: AppState;
}

// Provider component for corner radius state
export const CornerRadiusProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isIndividualMode, setIsIndividualMode] = useState(false);
  const [cornerValues, setCornerValues] = useState<CornerRadiusValues>({
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0,
  });

  const setCornerValue = (corner: keyof CornerRadiusValues, value: number) => {
    setCornerValues((prev) => ({
      ...prev,
      [corner]: Math.max(0, Math.round(value)),
    }));
  };

  const resetCornerValues = (value: number) => {
    setCornerValues({
      topLeft: value,
      topRight: value,
      bottomLeft: value,
      bottomRight: value,
    });
  };

  return (
    <CornerRadiusExpandedContext.Provider
      value={{
        isExpanded,
        setIsExpanded,
        cornerValues,
        setCornerValue,
        isIndividualMode,
        setIsIndividualMode,
        resetCornerValues,
      }}
    >
      {children}
    </CornerRadiusExpandedContext.Provider>
  );
};

// Hook to use the context
export const useCornerRadiusExpanded = () => {
  const context = useContext(CornerRadiusExpandedContext);
  if (!context) {
    throw new Error(
      "useCornerRadiusExpanded must be used within CornerRadiusProvider",
    );
  }
  return context;
};

const handleCornerRadiusChange: DragInputCallbackType<"width"> = ({
  nextValue,
  originalElements,
  scene,
  app,
}) => {
  if (nextValue === undefined) {
    return;
  }

  const allElements = scene.getNonDeletedElements();
  const newValue = Math.max(0, Math.round(nextValue));

  const updatedElements = changeProperty(
    allElements,
    app.state,
    (el) => {
      if (!originalElements.some((sel) => sel.id === el.id)) {
        return el; // leave untouched elements as-is
      }

      if (!canChangeRoundness(el.type)) {
        return el;
      }

      if (newValue === 0) {
        return newElementWith(el, {
          roundness: null,
        });
      }

      if (!el.roundness || el.roundness.type !== ROUNDNESS.ADAPTIVE_RADIUS) {
        return newElementWith(el, {
          roundness: {
            type: ROUNDNESS.ADAPTIVE_RADIUS,
            value: newValue,
          },
        });
      }

      return newElementWith(el, {
        roundness: {
          ...el.roundness,
          value: newValue,
        },
      });
    },
    true,
  );

  scene.replaceAllElements(updatedElements);

  app.syncActionResult({ captureUpdate: CaptureUpdateAction.IMMEDIATELY });

  scene.triggerUpdate();
};

// Function to handle individual corner radius changes
const handleIndividualCornerChange = (
  corner: keyof CornerRadiusValues,
  value: number,
  targetElements: readonly ExcalidrawElement[],
  scene: Scene,
  app: any,
  setCornerValue: (corner: keyof CornerRadiusValues, value: number) => void,
  cornerValues: CornerRadiusValues,
) => {
  // Update the context state
  setCornerValue(corner, value);

  const allElements = scene.getNonDeletedElements();
  const newValue = Math.max(0, Math.round(value));

  // Get updated corner values
  const updatedCorners = {
    ...cornerValues,
    [corner]: newValue,
  };

  const updatedElements = changeProperty(
    allElements,
    app.state,
    (el) => {
      if (!targetElements.some((sel) => sel.id === el.id)) {
        return el;
      }

      if (!canChangeRoundness(el.type)) {
        return el;
      }

      // Check if all corners are 0 (sharp)
      const allCornersZero = Object.values(updatedCorners).every(
        (val) => val === 0,
      );

      if (allCornersZero) {
        return newElementWith(el, {
          roundness: null,
        });
      }

      // Check if all corners have the same value
      const cornerValues = Object.values(updatedCorners);
      const allCornersSame = cornerValues.every(
        (val) => val === cornerValues[0],
      );

      if (allCornersSame) {
        // If all corners are the same, use uniform roundness
        return newElementWith(el, {
          roundness: {
            type: ROUNDNESS.ADAPTIVE_RADIUS,
            value: cornerValues[0],
          },
        });
      }

      // If corners have different values, use individual corners
      return newElementWith(el, {
        roundness: {
          type: ROUNDNESS.INDIVIDUAL_CORNERS,
          corners: updatedCorners,
        },
      });
    },
    true,
  );

  scene.replaceAllElements(updatedElements);
  app.syncActionResult({ captureUpdate: CaptureUpdateAction.IMMEDIATELY });
  scene.triggerUpdate();
};

const CornerRadius = ({
  elements,
  element,
  elementsMap,
  scene,
  appState,
}: CornerRadiusProps) => {
  const app = useApp();
  const setAppState = useExcalidrawSetAppState();
  const inputRef = useRef<HTMLInputElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const {
    isExpanded,
    setIsExpanded,
    cornerValues,
    setCornerValue,
    isIndividualMode,
    setIsIndividualMode,
  } = useCornerRadiusExpanded();

  const targetElements = useMemo(() => {
    return elements || (element ? [element] : []);
  }, [elements, element]);

  const value = useMemo(() => {
    const relevantElements = targetElements.filter((el) =>
      canChangeRoundness(el.type),
    );

    if (relevantElements.length === 0) {
      return DEFAULT_ADAPTIVE_RADIUS;
    }

    // Check if any elements have individual corners and if their values are mixed
    const allCornerValues: number[] = [];

    relevantElements.forEach((el) => {
      if (
        el.roundness?.type === ROUNDNESS.INDIVIDUAL_CORNERS &&
        el.roundness.corners
      ) {
        const corners = el.roundness.corners;
        allCornerValues.push(
          corners.topLeft,
          corners.topRight,
          corners.bottomLeft,
          corners.bottomRight,
        );
      } else {
        // For non-individual corner elements, treat as uniform value
        const uniformValue = !el.roundness
          ? 0
          : el.roundness.type === ROUNDNESS.PROPORTIONAL_RADIUS ||
            el.roundness.type === ROUNDNESS.LEGACY
          ? DEFAULT_ADAPTIVE_RADIUS
          : el.roundness.value ?? DEFAULT_ADAPTIVE_RADIUS;
        allCornerValues.push(
          uniformValue,
          uniformValue,
          uniformValue,
          uniformValue,
        );
      }
    });

    // Check if all corner values from elements are the same
    const uniqueCornerValues = new Set(allCornerValues);
    const hasUniformValue = uniqueCornerValues.size === 1;

    // If in individual mode, use context values for display
    if (isIndividualMode) {
      const contextValues = Object.values(cornerValues);
      const uniqueContextValues = new Set(contextValues);
      if (uniqueContextValues.size > 1) {
        return "Mixed";
      }
      return contextValues[0];
    }

    // If elements have mixed corner values, show "Mixed"
    if (!hasUniformValue) {
      return "Mixed";
    }

    // If all corner values are the same, return that value
    return allCornerValues[0];
  }, [targetElements, isIndividualMode, cornerValues]);

  const editable = targetElements.some((el) => canChangeRoundness(el.type));
  const hasFrames = targetElements.some((el) => isFrameLikeElement(el));

  const handleDragChange = (newValue: number) => {
    // Snap to 4px grid when dragging
    const snapTo4px = (value: number) => Math.round(value / 4) * 4;
    const clampedValue = Math.max(0, snapTo4px(newValue));

    if (isIndividualMode) {
      // In individual mode, set all corners to the same value
      Object.keys(cornerValues).forEach((corner) => {
        setCornerValue(corner as keyof CornerRadiusValues, clampedValue);
      });
    }

    handleCornerRadiusChange({
      accumulatedChange: 0,
      instantChange: 0,
      originalElements: targetElements,
      originalElementsMap: scene.getNonDeletedElementsMap(),
      shouldKeepAspectRatio: false,
      shouldChangeByStepSize: false,
      scene,
      nextValue: clampedValue,
      property: "width",
      originalAppState: appState,
      setInputValue: () => {},
      app,
      setAppState,
    });
  };

  if (!editable) {
    return null;
  }

  return (
    <div
      className="drag-input-container"
      data-testid="Corner Radius"
      title="Corner radius"
    >
      <div
        className="drag-input-label"
        ref={labelRef}
        onPointerDown={(event) => {
          if (inputRef.current && editable) {
            document.body.classList.add("excalidraw-cursor-resize");

            const startValue = typeof value === "number" ? value : 0;
            let lastPointer: { x: number; y: number } | null = null;
            let accumulatedChange = 0;

            const onPointerMove = (event: PointerEvent) => {
              if (lastPointer) {
                const instantChange = event.clientX - lastPointer.x;

                if (instantChange !== 0) {
                  // Make drag sensitive (2px movement = 1 unit change)
                  accumulatedChange += instantChange / 2;
                  const newValue = startValue + accumulatedChange;
                  handleDragChange(newValue);
                }
              }

              lastPointer = {
                x: event.clientX,
                y: event.clientY,
              };
            };

            const onPointerUp = () => {
              window.removeEventListener(
                EVENT.POINTER_MOVE,
                onPointerMove,
                false,
              );
              window.removeEventListener(EVENT.POINTER_UP, onPointerUp, false);
              document.body.classList.remove("excalidraw-cursor-resize");
              lastPointer = null;
              accumulatedChange = 0;
            };

            window.addEventListener(EVENT.POINTER_MOVE, onPointerMove, false);
            window.addEventListener(EVENT.POINTER_UP, onPointerUp, false);
          }
        }}
        onPointerEnter={() => {
          if (labelRef.current) {
            labelRef.current.style.cursor = "ew-resize";
          }
        }}
      >
        <InlineIcon icon={borderCornersIcon} />
      </div>
      <input
        ref={inputRef}
        className="drag-input"
        autoComplete="off"
        spellCheck="false"
        value={isInputFocused ? inputValue : value}
        onChange={(event) => {
          const newValue = event.target.value;
          setInputValue(newValue);

          // Allow empty values during typing
          if (newValue === "" || newValue === "Mixed") {
            return;
          }

          const nextValue = Math.max(0, Math.round(Number(newValue)));
          if (!isNaN(nextValue)) {
            if (isIndividualMode) {
              // In individual mode, set all corners to the same value
              Object.keys(cornerValues).forEach((corner) => {
                setCornerValue(corner as keyof CornerRadiusValues, nextValue);
              });
            }

            handleCornerRadiusChange({
              accumulatedChange: 0,
              instantChange: 0,
              originalElements: targetElements,
              originalElementsMap: scene.getNonDeletedElementsMap(),
              shouldKeepAspectRatio: false,
              shouldChangeByStepSize: false,
              scene,
              nextValue,
              property: "width",
              originalAppState: appState,
              setInputValue: () => {},
              app,
              setAppState,
            });
          }
        }}
        onBlur={(event) => {
          setIsInputFocused(false);
          const newValue = event.target.value;
          // If empty on blur, set to 0
          if (newValue === "" || newValue === "Mixed") {
            const nextValue = 0;

            if (isIndividualMode) {
              // In individual mode, set all corners to 0
              Object.keys(cornerValues).forEach((corner) => {
                setCornerValue(corner as keyof CornerRadiusValues, nextValue);
              });
            }

            handleCornerRadiusChange({
              accumulatedChange: 0,
              instantChange: 0,
              originalElements: targetElements,
              originalElementsMap: scene.getNonDeletedElementsMap(),
              shouldKeepAspectRatio: false,
              shouldChangeByStepSize: false,
              scene,
              nextValue,
              property: "width",
              originalAppState: appState,
              setInputValue: () => {},
              app,
              setAppState,
            });
          }
        }}
        onFocus={(event) => {
          setIsInputFocused(true);
          setInputValue(String(value));
          event.target.select();
        }}
        onKeyDown={(event) => {
          if (event.key === KEYS.ENTER) {
            (event.target as HTMLInputElement).blur();
            app.focusContainer();
          } else if (
            event.key === KEYS.ARROW_UP ||
            event.key === KEYS.ARROW_DOWN
          ) {
            event.preventDefault();

            const currentValue = typeof value === "number" ? value : 0;
            let nextValue: number;

            if (event.shiftKey) {
              // Shift + arrow: snap to base unit of 4 (4, 8, 12, 16...)
              if (event.key === KEYS.ARROW_UP) {
                nextValue = Math.ceil(currentValue / 4) * 4;
                // If already on a multiple of 4, go to next multiple
                if (nextValue === currentValue) {
                  nextValue = currentValue + 4;
                }
              } else {
                nextValue = Math.floor(currentValue / 4) * 4;
                // If already on a multiple of 4, go to previous multiple
                if (nextValue === currentValue) {
                  nextValue = currentValue - 4;
                }
              }
            } else if (event.key === KEYS.ARROW_UP) {
              // Regular arrow: round to closest whole number
              nextValue = Math.ceil(currentValue);
              // If already whole number, increment by 1
              if (nextValue === currentValue) {
                nextValue = currentValue + 1;
              }
            } else {
              nextValue = Math.floor(currentValue);
              // If already whole number, decrement by 1
              if (nextValue === currentValue) {
                nextValue = currentValue - 1;
              }
            }

            // Ensure minimum value of 0
            nextValue = Math.max(0, nextValue);

            // Update the input display value immediately
            setInputValue(String(nextValue));

            if (isIndividualMode) {
              // In individual mode, set all corners to the same value
              Object.keys(cornerValues).forEach((corner) => {
                setCornerValue(corner as keyof CornerRadiusValues, nextValue);
              });
            }

            handleCornerRadiusChange({
              accumulatedChange: 0,
              instantChange: 0,
              originalElements: targetElements,
              originalElementsMap: scene.getNonDeletedElementsMap(),
              shouldKeepAspectRatio: false,
              shouldChangeByStepSize: false,
              scene,
              nextValue,
              property: "width",
              originalAppState: appState,
              setInputValue: () => {},
              app,
              setAppState,
            });
          }
        }}
        disabled={!editable}
      />
      {/* Don't show expand button for frames */}
      {!hasFrames && (
        <button
          className="drag-input-icon-right-button"
          type="button"
          onClick={() => {
            if (!isExpanded) {
              // When expanding, enter individual mode and sync current values
              setIsIndividualMode(true);

              // Sync corner values from current element
              const firstElement = targetElements.find((el) =>
                canChangeRoundness(el.type),
              );
              if (
                firstElement?.roundness?.type ===
                  ROUNDNESS.INDIVIDUAL_CORNERS &&
                firstElement.roundness.corners
              ) {
                // If element already has individual corners, use those values
                const corners = firstElement.roundness.corners;
                setCornerValue("topLeft", corners.topLeft);
                setCornerValue("topRight", corners.topRight);
                setCornerValue("bottomLeft", corners.bottomLeft);
                setCornerValue("bottomRight", corners.bottomRight);
              } else {
                // Otherwise, use current uniform value for all corners
                const currentValue = typeof value === "number" ? value : 0;
                Object.keys(cornerValues).forEach((corner) => {
                  setCornerValue(
                    corner as keyof CornerRadiusValues,
                    currentValue,
                  );
                });
              }
            } else {
              // When collapsing, exit individual mode
              setIsIndividualMode(false);
            }
            setIsExpanded(!isExpanded);
          }}
        >
          <InlineIcon icon={adjustmentsHorizontalIcon} />
        </button>
      )}
    </div>
  );
};

// Separate component for the expandable section
export const CornerRadiusExpanded = ({
  elements,
  element,
  elementsMap,
  scene,
  appState,
}: CornerRadiusProps) => {
  const { isExpanded, cornerValues, setCornerValue } =
    useCornerRadiusExpanded();
  const app = useApp();
  const [inputValues, setInputValues] = useState({
    topLeft: "",
    topRight: "",
    bottomLeft: "",
    bottomRight: "",
  });
  const [focusedInput, setFocusedInput] = useState<
    keyof CornerRadiusValues | null
  >(null);

  const targetElements = useMemo(() => {
    return elements || (element ? [element] : []);
  }, [elements, element]);

  const editable = targetElements.some((el) => canChangeRoundness(el.type));
  const hasFrames = targetElements.some((el) => isFrameLikeElement(el));

  // Don't show individual corner controls for frames
  if (!isExpanded || !editable || hasFrames) {
    return null;
  }

  const handleCornerChange = (
    corner: keyof CornerRadiusValues,
    value: string,
  ) => {
    setInputValues((prev) => ({ ...prev, [corner]: value }));

    // Allow empty values during typing
    if (value === "") {
      return;
    }

    const numValue = Math.max(0, Math.round(Number(value)));
    if (!isNaN(numValue)) {
      handleIndividualCornerChange(
        corner,
        numValue,
        targetElements,
        scene,
        app,
        setCornerValue,
        cornerValues,
      );
    }
  };

  const handleCornerFocus = (corner: keyof CornerRadiusValues) => {
    setFocusedInput(corner);
    setInputValues((prev) => ({
      ...prev,
      [corner]: String(cornerValues[corner]),
    }));
  };

  const handleCornerBlur = (
    corner: keyof CornerRadiusValues,
    value: string,
  ) => {
    setFocusedInput(null);
    // If empty on blur, set to 0
    if (value === "") {
      const numValue = 0;
      handleIndividualCornerChange(
        corner,
        numValue,
        targetElements,
        scene,
        app,
        setCornerValue,
        cornerValues,
      );
    }
  };

  const handleCornerKeyDown = (
    corner: keyof CornerRadiusValues,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === KEYS.ENTER) {
      (event.target as HTMLInputElement).blur();
      app.focusContainer();
    } else if (event.key === KEYS.ARROW_UP || event.key === KEYS.ARROW_DOWN) {
      event.preventDefault();

      const currentValue = cornerValues[corner];
      let nextValue: number;

      if (event.shiftKey) {
        // Shift + arrow: snap to base unit of 4 (4, 8, 12, 16...)
        if (event.key === KEYS.ARROW_UP) {
          nextValue = Math.ceil(currentValue / 4) * 4;
          // If already on a multiple of 4, go to next multiple
          if (nextValue === currentValue) {
            nextValue = currentValue + 4;
          }
        } else {
          nextValue = Math.floor(currentValue / 4) * 4;
          // If already on a multiple of 4, go to previous multiple
          if (nextValue === currentValue) {
            nextValue = currentValue - 4;
          }
        }
      } else if (event.key === KEYS.ARROW_UP) {
        // Regular arrow: round to closest whole number
        nextValue = Math.ceil(currentValue);
        // If already whole number, increment by 1
        if (nextValue === currentValue) {
          nextValue = currentValue + 1;
        }
      } else {
        nextValue = Math.floor(currentValue);
        // If already whole number, decrement by 1
        if (nextValue === currentValue) {
          nextValue = currentValue - 1;
        }
      }

      // Ensure minimum value of 0
      nextValue = Math.max(0, nextValue);

      // Update the input value immediately for visual feedback
      setInputValues((prev) => ({
        ...prev,
        [corner]: String(nextValue),
      }));

      handleIndividualCornerChange(
        corner,
        nextValue,
        targetElements,
        scene,
        app,
        setCornerValue,
        cornerValues,
      );
    }
  };

  const handleCornerDrag = (
    corner: keyof CornerRadiusValues,
    newValue: number,
  ) => {
    // Snap to 4px grid when dragging
    const snapTo4px = (value: number) => Math.round(value / 4) * 4;
    const clampedValue = Math.max(0, snapTo4px(newValue));

    handleIndividualCornerChange(
      corner,
      clampedValue,
      targetElements,
      scene,
      app,
      setCornerValue,
      cornerValues,
    );
  };

  return (
    <div style={{ marginTop: "0.5rem" }}>
      {/* Top row - Top Left and Top Right */}
      <div className="property-row-dual">
        <div className="property-item">
          <div className="drag-input-container">
            <div
              className="drag-input-label"
              onPointerDown={(event) => {
                if (editable) {
                  document.body.classList.add("excalidraw-cursor-resize");

                  const startValue = cornerValues.topLeft;
                  let lastPointer: { x: number; y: number } | null = null;
                  let accumulatedChange = 0;

                  const onPointerMove = (event: PointerEvent) => {
                    if (lastPointer) {
                      const instantChange = event.clientX - lastPointer.x;

                      if (instantChange !== 0) {
                        // Make drag sensitive (2px movement = 1 unit change)
                        accumulatedChange += instantChange / 2;
                        const newValue = startValue + accumulatedChange;
                        handleCornerDrag("topLeft", newValue);
                      }
                    }

                    lastPointer = {
                      x: event.clientX,
                      y: event.clientY,
                    };
                  };

                  const onPointerUp = () => {
                    window.removeEventListener(
                      EVENT.POINTER_MOVE,
                      onPointerMove,
                      false,
                    );
                    window.removeEventListener(
                      EVENT.POINTER_UP,
                      onPointerUp,
                      false,
                    );
                    document.body.classList.remove("excalidraw-cursor-resize");
                    lastPointer = null;
                    accumulatedChange = 0;
                  };

                  window.addEventListener(
                    EVENT.POINTER_MOVE,
                    onPointerMove,
                    false,
                  );
                  window.addEventListener(EVENT.POINTER_UP, onPointerUp, false);
                }
              }}
              onPointerEnter={(e) => {
                if (editable) {
                  (e.target as HTMLElement).style.cursor = "ew-resize";
                }
              }}
            >
              <InlineIcon icon={radiusTopLeftIcon} />
            </div>
            <input
              className="drag-input"
              autoComplete="off"
              spellCheck="false"
              value={
                focusedInput === "topLeft"
                  ? inputValues.topLeft
                  : cornerValues.topLeft
              }
              onChange={(event) =>
                handleCornerChange("topLeft", event.target.value)
              }
              onBlur={(event) =>
                handleCornerBlur("topLeft", event.target.value)
              }
              onFocus={(event) => {
                handleCornerFocus("topLeft");
                event.target.select();
              }}
              onKeyDown={(event) => handleCornerKeyDown("topLeft", event)}
              disabled={!editable}
            />
          </div>
        </div>
        <div className="property-item">
          <div className="drag-input-container">
            <div
              className="drag-input-label"
              onPointerDown={(event) => {
                if (editable) {
                  document.body.classList.add("excalidraw-cursor-resize");

                  const startValue = cornerValues.topRight;
                  let lastPointer: { x: number; y: number } | null = null;
                  let accumulatedChange = 0;

                  const onPointerMove = (event: PointerEvent) => {
                    if (lastPointer) {
                      const instantChange = event.clientX - lastPointer.x;

                      if (instantChange !== 0) {
                        // Make drag sensitive (2px movement = 1 unit change)
                        accumulatedChange += instantChange / 2;
                        const newValue = startValue + accumulatedChange;
                        handleCornerDrag("topRight", newValue);
                      }
                    }

                    lastPointer = {
                      x: event.clientX,
                      y: event.clientY,
                    };
                  };

                  const onPointerUp = () => {
                    window.removeEventListener(
                      EVENT.POINTER_MOVE,
                      onPointerMove,
                      false,
                    );
                    window.removeEventListener(
                      EVENT.POINTER_UP,
                      onPointerUp,
                      false,
                    );
                    document.body.classList.remove("excalidraw-cursor-resize");
                    lastPointer = null;
                    accumulatedChange = 0;
                  };

                  window.addEventListener(
                    EVENT.POINTER_MOVE,
                    onPointerMove,
                    false,
                  );
                  window.addEventListener(EVENT.POINTER_UP, onPointerUp, false);
                }
              }}
              onPointerEnter={(e) => {
                if (editable) {
                  (e.target as HTMLElement).style.cursor = "ew-resize";
                }
              }}
            >
              <InlineIcon icon={radiusTopRightIcon} />
            </div>
            <input
              className="drag-input"
              autoComplete="off"
              spellCheck="false"
              value={
                focusedInput === "topRight"
                  ? inputValues.topRight
                  : cornerValues.topRight
              }
              onChange={(event) =>
                handleCornerChange("topRight", event.target.value)
              }
              onBlur={(event) =>
                handleCornerBlur("topRight", event.target.value)
              }
              onFocus={(event) => {
                handleCornerFocus("topRight");
                event.target.select();
              }}
              onKeyDown={(event) => handleCornerKeyDown("topRight", event)}
              disabled={!editable}
            />
          </div>
        </div>
      </div>

      {/* Bottom row - Bottom Left and Bottom Right */}
      <div className="property-row-dual" style={{ marginTop: "0.5rem" }}>
        <div className="property-item">
          <div className="drag-input-container">
            <div
              className="drag-input-label"
              onPointerDown={(event) => {
                if (editable) {
                  document.body.classList.add("excalidraw-cursor-resize");

                  const startValue = cornerValues.bottomLeft;
                  let lastPointer: { x: number; y: number } | null = null;
                  let accumulatedChange = 0;

                  const onPointerMove = (event: PointerEvent) => {
                    if (lastPointer) {
                      const instantChange = event.clientX - lastPointer.x;

                      if (instantChange !== 0) {
                        // Make drag sensitive (2px movement = 1 unit change)
                        accumulatedChange += instantChange / 2;
                        const newValue = startValue + accumulatedChange;
                        handleCornerDrag("bottomLeft", newValue);
                      }
                    }

                    lastPointer = {
                      x: event.clientX,
                      y: event.clientY,
                    };
                  };

                  const onPointerUp = () => {
                    window.removeEventListener(
                      EVENT.POINTER_MOVE,
                      onPointerMove,
                      false,
                    );
                    window.removeEventListener(
                      EVENT.POINTER_UP,
                      onPointerUp,
                      false,
                    );
                    document.body.classList.remove("excalidraw-cursor-resize");
                    lastPointer = null;
                    accumulatedChange = 0;
                  };

                  window.addEventListener(
                    EVENT.POINTER_MOVE,
                    onPointerMove,
                    false,
                  );
                  window.addEventListener(EVENT.POINTER_UP, onPointerUp, false);
                }
              }}
              onPointerEnter={(e) => {
                if (editable) {
                  (e.target as HTMLElement).style.cursor = "ew-resize";
                }
              }}
            >
              <InlineIcon icon={radiusBottomLeftIcon} />
            </div>
            <input
              className="drag-input"
              autoComplete="off"
              spellCheck="false"
              value={
                focusedInput === "bottomLeft"
                  ? inputValues.bottomLeft
                  : cornerValues.bottomLeft
              }
              onChange={(event) =>
                handleCornerChange("bottomLeft", event.target.value)
              }
              onBlur={(event) =>
                handleCornerBlur("bottomLeft", event.target.value)
              }
              onFocus={(event) => {
                handleCornerFocus("bottomLeft");
                event.target.select();
              }}
              onKeyDown={(event) => handleCornerKeyDown("bottomLeft", event)}
              disabled={!editable}
            />
          </div>
        </div>
        <div className="property-item">
          <div className="drag-input-container">
            <div
              className="drag-input-label"
              onPointerDown={(event) => {
                if (editable) {
                  document.body.classList.add("excalidraw-cursor-resize");

                  const startValue = cornerValues.bottomRight;
                  let lastPointer: { x: number; y: number } | null = null;
                  let accumulatedChange = 0;

                  const onPointerMove = (event: PointerEvent) => {
                    if (lastPointer) {
                      const instantChange = event.clientX - lastPointer.x;

                      if (instantChange !== 0) {
                        // Make drag sensitive (2px movement = 1 unit change)
                        accumulatedChange += instantChange / 2;
                        const newValue = startValue + accumulatedChange;
                        handleCornerDrag("bottomRight", newValue);
                      }
                    }

                    lastPointer = {
                      x: event.clientX,
                      y: event.clientY,
                    };
                  };

                  const onPointerUp = () => {
                    window.removeEventListener(
                      EVENT.POINTER_MOVE,
                      onPointerMove,
                      false,
                    );
                    window.removeEventListener(
                      EVENT.POINTER_UP,
                      onPointerUp,
                      false,
                    );
                    document.body.classList.remove("excalidraw-cursor-resize");
                    lastPointer = null;
                    accumulatedChange = 0;
                  };

                  window.addEventListener(
                    EVENT.POINTER_MOVE,
                    onPointerMove,
                    false,
                  );
                  window.addEventListener(EVENT.POINTER_UP, onPointerUp, false);
                }
              }}
              onPointerEnter={(e) => {
                if (editable) {
                  (e.target as HTMLElement).style.cursor = "ew-resize";
                }
              }}
            >
              <InlineIcon icon={radiusBottomRightIcon} />
            </div>
            <input
              className="drag-input"
              autoComplete="off"
              spellCheck="false"
              value={
                focusedInput === "bottomRight"
                  ? inputValues.bottomRight
                  : cornerValues.bottomRight
              }
              onChange={(event) =>
                handleCornerChange("bottomRight", event.target.value)
              }
              onBlur={(event) =>
                handleCornerBlur("bottomRight", event.target.value)
              }
              onFocus={(event) => {
                handleCornerFocus("bottomRight");
                event.target.select();
              }}
              onKeyDown={(event) => handleCornerKeyDown("bottomRight", event)}
              disabled={!editable}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CornerRadius;
